import React, { useState, useEffect } from "react";
import { MessageCircle, LogOut, RefreshCw, CheckCircle, XCircle } from "lucide-react";
import secureStorage from "../../utils/secureStorage";
import Notification from "../Common/Notification";
import { whatsappApi } from "../../config/api";
import BookingWhatsAppTemplate from "./BookingWhatsAppTemplate";
import WhatsAppTestSend from "./WhatsAppTestSend";
import {SettingsForm, FormSection, SwitchRow, SectionActions} from "./SettingsUI";
import Button from "../Common/Button";

const WhatsAppSettings = () => {
    const [notification, setNotification] = useState({
        show: false,
        type: "success",
        message: "",
    });
    const [accountUuid, setAccountUuid] = useState(null);
    const [qrCode, setQrCode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState("disconnected");
    const [forceReconnect, setForceReconnect] = useState(false);

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        setTimeout(() => {
            setNotification({ show: false, type: "", message: "" });
        }, 3000);
    };

    // Récupérer l'UUID du compte au chargement
    useEffect(() => {
        const loadAccountInfo = async () => {
            try {
                const account = await secureStorage.getItem("selectedAccount");
                if (account && account.id) {
                    setAccountUuid(account.id);
                    // Check connection status immediately after loading account
                    await checkConnectionStatus(account.id);
                }
            } catch (error) {
                console.error("Erreur lors du chargement des informations du compte:", error);
                showNotification("error", "Impossible de charger les informations du compte");
            }
        };

        loadAccountInfo();
    }, []);

    // Shared connection check helper (used on load + periodic polling)
    const checkConnectionStatus = async (uuid) => {
        const id = uuid || accountUuid;
        if (!id) return;
        try {
            const statusResponse = await whatsappApi.get(`/check-connection/${id}`);
            const data = statusResponse.data?.data;
            if (data?.connected) {
                setIsConnected(true);
                setConnectionStatus("connected");
                setQrCode(null);
                return true;
            }
        } catch (err) {
            // Silent – server may not have a session yet
        }
        return false;
    };

    // Fonction pour générer le QR Code
    const handleConnectWhatsApp = async () => {
        if (!accountUuid) {
            showNotification("error", "UUID du compte non disponible");
            return;
        }

        try {
            setIsLoading(true);
            setConnectionStatus("connecting");
            showNotification("loading", "Génération du QR Code...");

            // Si forceReconnect est activé, se déconnecter d'abord
            if (forceReconnect && isConnected) {
                await whatsappApi.get(`/logout/${accountUuid}`);
                setIsConnected(false);
                setQrCode(null);
            }

            const response = await whatsappApi.get(`/generate-qr/${accountUuid}`);

            // Vérifier si l'appareil est déjà connecté (status: false avec message "Device already connected")
            if (response.data && response.data.status === false &&
                response.data.message === "Device already connected") {

                showNotification("info", "Appareil déjà connecté. Déconnexion en cours...");

                const logoutResponse = await whatsappApi.get(`/logout/${accountUuid}`);

                if (logoutResponse.data && logoutResponse.data.status === true) {
                    showNotification("success", "Déconnexion réussie. Régénération du QR Code...");

                    await new Promise(resolve => setTimeout(resolve, 1000));

                    const newQrResponse = await whatsappApi.get(`/generate-qr/${accountUuid}`);
                    const newQrCode = newQrResponse.data?.data?.qrCode;

                    if (newQrCode) {
                        setQrCode(newQrCode);
                        setConnectionStatus("waiting");
                        setIsConnected(false);
                        showNotification("success", "Nouveau QR Code généré ! Scannez-le avec WhatsApp");
                        startConnectionCheck();
                    } else {
                        throw new Error("Impossible de générer un nouveau QR Code");
                    }
                } else {
                    throw new Error("Échec de la déconnexion");
                }
                return;
            }

            // Vérifier si l'appareil est déjà connecté (status: true avec message "device  connect")
            if (response.data && response.data.status === true && response.data.message === "device  connect") {
                setIsConnected(true);
                setConnectionStatus("connected");
                setQrCode(null);
                showNotification("success", "WhatsApp est déjà connecté !");
                setIsLoading(false);
                return;
            }

            const qrCode = response.data?.data?.qrCode;

            if (qrCode) {
                setQrCode(qrCode);
                setConnectionStatus("waiting");
                showNotification("success", "QR Code généré ! Scannez-le avec WhatsApp");
                startConnectionCheck();
            } else {
                throw new Error("QR Code non reçu");
            }
        } catch (error) {
            console.error("Erreur lors de la génération du QR Code:", error);
            setConnectionStatus("disconnected");
            const errorMessage = error.response?.data?.message ||
                                error.message ||
                                "Impossible de générer le QR Code";
            showNotification("error", errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Fonction pour démarrer la vérification périodique de la connexion
    const startConnectionCheck = () => {
        const checkInterval = setInterval(async () => {
            try {
                const statusResponse = await whatsappApi.get(`/check-connection/${accountUuid}`);
                const data = statusResponse.data?.data;
                if (data?.connected) {
                    setIsConnected(true);
                    setConnectionStatus("connected");
                    setQrCode(null);
                    clearInterval(checkInterval);
                    showNotification("success", "WhatsApp connecté avec succès !");
                }
            } catch (err) {
                console.error("Erreur lors de la vérification du statut:", err);
            }
        }, 3000);

        // Arrêter la vérification après 2 minutes
        setTimeout(() => {
            clearInterval(checkInterval);
            if (!isConnected) {
                showNotification("error", "Délai de connexion dépassé. Veuillez réessayer.");
                setConnectionStatus("disconnected");
            }
        }, 120000);
    };

    // Fonction pour se déconnecter
    const handleLogout = async () => {
        if (!accountUuid) {
            showNotification("error", "UUID du compte non disponible");
            return;
        }

        try {
            setIsLoading(true);
            showNotification("loading", "Déconnexion en cours...");

            await whatsappApi.get(`/logout/${accountUuid}`);

            setQrCode(null);
            setIsConnected(false);
            setConnectionStatus("disconnected");
            showNotification("success", "Déconnecté avec succès de WhatsApp");
        } catch (error) {
            console.error("Erreur lors de la déconnexion:", error);
            const errorMessage = error.response?.data?.message ||
                                error.message ||
                                "Impossible de se déconnecter";
            showNotification("error", errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Fonction pour vérifier le statut de connexion (optionnel)
    const handleCheckStatus = async () => {
        if (!accountUuid) {
            showNotification("error", "UUID du compte non disponible");
            return;
        }

        try {
            setIsLoading(true);
            const response = await whatsappApi.get(`/check-connection/${accountUuid}`);
            const data = response.data?.data;

            if (data?.connected) {
                setIsConnected(true);
                setConnectionStatus("connected");
                setQrCode(null);
                showNotification("success", "WhatsApp est connecté");
            } else {
                setIsConnected(false);
                setConnectionStatus("disconnected");
                showNotification("info", "WhatsApp n'est pas connecté");
            }
        } catch (error) {
            console.error("Erreur lors de la vérification du statut:", error);
            showNotification("error", "Impossible de vérifier le statut");
        } finally {
            setIsLoading(false);
        }
    };

    const STATUS = {
        connected: {label: "Connecté", Icon: CheckCircle, className: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"},
        disconnected: {label: "Non connecté", Icon: XCircle, className: "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"},
        waiting: {label: "En attente de scan...", Icon: RefreshCw, className: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300", spin: true},
    };
    const status = STATUS[connectionStatus] || STATUS.disconnected;

    return (
        <SettingsForm>
            {notification.show && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    show={notification.show}
                />
            )}

            <SectionActions>
                <Button variant="plain" size="sm" icon={RefreshCw} compact disabled={isLoading} onClick={handleCheckStatus}>
                    Vérifier le statut
                </Button>
                {(!isConnected && !qrCode) && (
                    <Button variant="plain" size="sm" icon={MessageCircle} loading={isLoading} disabled={!accountUuid}
                            onClick={handleConnectWhatsApp}>
                        {isLoading ? "Chargement..." : "Connecter"}
                    </Button>
                )}
                {(qrCode && !isConnected) && (
                    <Button variant="plain" size="sm" icon={RefreshCw} loading={isLoading} onClick={handleConnectWhatsApp}>
                        Régénérer le QR Code
                    </Button>
                )}
                {isConnected && (
                    <Button variant="danger" size="sm" icon={LogOut} loading={isLoading} onClick={handleLogout}>
                        {isLoading ? "Déconnexion..." : "Se déconnecter"}
                    </Button>
                )}
            </SectionActions>

            <FormSection
                title="Connexion WhatsApp"
                description="Connectez votre compte WhatsApp pour envoyer des messages automatiques à vos clients."
            >
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${status.className}`}>
                    <status.Icon className={`w-3.5 h-3.5 ${status.spin ? "animate-spin" : ""}`}/>
                    {status.label}
                </span>

                {qrCode && (
                    <div className="flex flex-col items-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-white mb-4">
                            Scannez ce QR Code avec WhatsApp
                        </h4>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <img src={qrCode} alt="QR Code WhatsApp" className="w-64 h-64"/>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 text-center max-w-md">
                            Ouvrez WhatsApp sur votre téléphone → Paramètres → Appareils connectés → Connecter un appareil
                        </p>
                    </div>
                )}

                {!isConnected && (
                    <SwitchRow
                        id="whatsappForceReconnect"
                        label="Forcer la reconnexion"
                        description="Si vous rencontrez des problèmes : déconnecte le compte existant avant de générer un nouveau QR Code."
                        checked={forceReconnect}
                        onChange={setForceReconnect}
                    />
                )}

                {accountUuid && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        UUID du compte : <span className="font-mono text-gray-700 dark:text-gray-300">{accountUuid}</span>
                    </p>
                )}
            </FormSection>

            {/* Custom WhatsApp confirmation of online bookings */}
            <BookingWhatsAppTemplate showNotification={showNotification} />

            {isConnected && <WhatsAppTestSend accountUuid={accountUuid} showNotification={showNotification}/>}
        </SettingsForm>
    );
};

export default WhatsAppSettings;

